import React, { useState, useEffect } from 'react';
import styles from './Subscriptions.module.scss';
import Table from '../../../components/table/Table';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Modal from '../../../components/modal/Modal';
import Filter from '../../../components/filter/Filter';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const STATUS_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
];

const TYPE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Trial', value: 'true' },
  { label: 'Regular', value: 'false' },
];

const Subscriptions = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingRow, setTogglingRow] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false
  });

  const fetchSubscriptions = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 10, search };
      if (statusFilter !== '') params.status = statusFilter;
      if (typeFilter !== '') params.isTrail = typeFilter;
      const response = await axiosClient.get('/subscription', { params });
      const responseData = response.data;
      setData(responseData.subscriptions || []);
      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages
      });
    } catch (error) {
      showToast(error.message || 'Failed to fetch subscriptions', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, search, statusFilter, typeFilter]);

  useEffect(() => {
    fetchSubscriptions(1);
  }, [fetchSubscriptions]);

  const handleToggleStatus = async () => {
    setIsToggling(true);
    try {
      await axiosClient.patch(`/subscription/${togglingRow.id}/toggle-status`);
      showToast(`Subscription ${togglingRow.status ? 'deactivated' : 'activated'} successfully`);
      setTogglingRow(null);
      fetchSubscriptions(pagination.current);
    } catch (error) {
      showToast(error.message || 'Failed to update status', 'error');
    } finally {
      setIsToggling(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axiosClient.get('/subscription/export', {
        responseType: 'blob',
        transformResponse: [(data) => data],
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'subscriptions.xlsx';
      link.click();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosClient.delete(`/subscription/${deletingId}`);
      showToast('Subscription deleted successfully');
      setDeletingId(null);
      fetchSubscriptions(pagination.current);
    } catch (error) {
      showToast(error.message || 'Failed to delete subscription', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      header: 'Name',
      key: 'name',
      render: (row) => (
        <span className={styles.nameLink} onClick={() => navigate(`/subscriptions/edit/${row.id}`)}>
          {row.name}
        </span>
      ),
    },
    {
      header: 'Type',
      key: 'isTrail',
      render: (row) => (
        <div className={`${styles.badge} ${row.isTrail ? styles.badgeTrial : styles.badgeRegular}`}>
          {row.isTrail ? 'Trial' : 'Regular'}
        </div>
      ),
    },
    {
      header: 'Status',
      key: 'status',
      render: (row) => (
        <div className={`${styles.statusBadge} ${row.status ? styles.statusActive : styles.statusInactive}`}>
          <span className={styles.statusDot} />
          {row.status ? 'Active' : 'Inactive'}
        </div>
      ),
    },
    {
      header: 'Start Date',
      key: 'startDate',
      render: (row) => new Date(row.startDate).toLocaleDateString(),
    },
    {
      header: 'End Date',
      key: 'endDate',
      render: (row) => new Date(row.endDate).toLocaleDateString(),
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Subscriptions" />
        <div className={styles.headerActions}>
          <Button
            label={
              <div className={styles.cta}>
                <Icon name="arrow-up-right" />
                {isExporting ? 'Exporting...' : 'Export'}
              </div>
            }
            variant="primary-light"
            size="medium"
            onClick={handleExport}
            disabled={isExporting}
          />
          <Search onSubmit={(value) => setSearch(value)} />
          <Button
            label={
              <div className={styles.cta}>
                <Icon name="plus" />
                Create Subscription
              </div>
            }
            variant="primary"
            onClick={() => navigate('/subscriptions/create')}
            size="medium"
          />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={STATUS_FILTERS}
              selected={statusFilter}
              onChange={(value) => setStatusFilter(value)}
            />
          </div>
          <div className={styles.typeFilter}>
            <Filter
              filters={TYPE_FILTERS}
              selected={typeFilter}
              onChange={(value) => setTypeFilter(value)}
            />
          </div>
        </div>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={(page) => fetchSubscriptions(page)}
          emptyMessage="No subscriptions found."
          getActionItems={(row) => [
            { label: 'Edit', onClick: () => navigate(`/subscriptions/edit/${row.id}`) },
            { label: row.status ? 'Deactivate' : 'Activate', onClick: () => setTogglingRow(row) },
            { label: 'Delete', onClick: () => setDeletingId(row.id) },
          ]}
        />
      </div>

      {deletingId && (
        <Modal>
          <div className={styles.confirmModal}>
            {isDeleting && <LoadingOverlay />}
            <h2 className={styles.confirmTitle}>Are you sure you want to delete this subscription?</h2>
            <div className={styles.confirmButtons}>
              <Button label="Delete" size="small" variant="secondary" onClick={handleDelete} disabled={isDeleting} />
              <Button label="Cancel" size="small" variant="cancel" onClick={() => setDeletingId(null)} />
            </div>
          </div>
        </Modal>
      )}

      {togglingRow && (
        <Modal>
          <div className={styles.confirmModal}>
            {isToggling && <LoadingOverlay />}
            <h2 className={styles.confirmTitle}>
              Are you sure you want to {togglingRow.status ? 'deactivate' : 'activate'} this subscription?
            </h2>
            <div className={styles.confirmButtons}>
              <Button
                label={togglingRow.status ? 'Deactivate' : 'Activate'}
                size="small"
                variant="secondary"
                onClick={handleToggleStatus}
                disabled={isToggling}
              />
              <Button label="Cancel" size="small" variant="cancel" onClick={() => setTogglingRow(null)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Subscriptions;
